import {
  type Hint,
  type ScoredHint,
  Rarity,
  DegenerateType,
  PowerUpTier,
} from "./types";
import { evaluateGuess } from "./evaluator";

const CANONICAL_RARITY_CUTOFFS: readonly [number, number, number, number] = [
  0.9, 0.75, 0.5, 0.25,
];

export function classifyDegenerate(
  solution: readonly number[],
  guess: readonly number[],
  placed: number,
  misplaced: number,
): DegenerateType {
  const n = solution.length;
  const guessSet = new Set(guess);

  if (guessSet.size === 1) {
    return DegenerateType.REPEATED_DIGITS;
  }

  if (placed === n - 1) {
    return DegenerateType.NEAR_SOLUTION;
  }

  if (placed === 0 && misplaced === n) {
    const sortedSolution = [...solution].sort((a, b) => a - b);
    const sortedGuess = [...guess].sort((a, b) => a - b);
    if (sortedSolution.every((d, i) => d === sortedGuess[i])) {
      return DegenerateType.ANAGRAM;
    }
  }

  const solutionSet = new Set(solution);
  const overlap = [...solutionSet].filter((d) => guessSet.has(d)).length;
  if (overlap >= n - 1 && placed + misplaced >= n - 1) {
    return DegenerateType.PARTIAL_ANAGRAM;
  }

  return DegenerateType.NONE;
}

export function classifyPowerUp(
  degenerate: DegenerateType,
): PowerUpTier | null {
  const mapping: Record<DegenerateType, PowerUpTier | null> = {
    [DegenerateType.NONE]: null,
    [DegenerateType.REPEATED_DIGITS]: PowerUpTier.BOOST,
    [DegenerateType.PARTIAL_ANAGRAM]: PowerUpTier.SURGE_I,
    [DegenerateType.NEAR_SOLUTION]: PowerUpTier.SURGE_II,
    [DegenerateType.ANAGRAM]: PowerUpTier.ORACLE,
  };
  return mapping[degenerate];
}

export function classifyRarity(
  percentile: number,
  cutoffs: readonly [number, number, number, number] = CANONICAL_RARITY_CUTOFFS,
): Rarity {
  if (percentile >= cutoffs[0]) return Rarity.LEGENDARY;
  if (percentile >= cutoffs[1]) return Rarity.EPIC;
  if (percentile >= cutoffs[2]) return Rarity.RARE;
  if (percentile >= cutoffs[3]) return Rarity.UNCOMMON;
  return Rarity.COMMON;
}

function generateAllGuesses(
  codeLength: number,
  digitRange: [number, number],
): number[][] {
  const digits: number[] = [];
  for (let d = digitRange[0]; d <= digitRange[1]; d++) {
    digits.push(d);
  }

  const totalSpace = Math.pow(digits.length, codeLength);
  const guesses: number[][] = [];

  for (let n = 0; n < totalSpace; n++) {
    const candidate: number[] = [];
    let remaining = n;
    for (let i = codeLength - 1; i >= 0; i--) {
      candidate[i] = digits[remaining % digits.length];
      remaining = Math.floor(remaining / digits.length);
    }
    guesses.push(candidate);
  }

  return guesses;
}

function arraysEqual(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function computeEliminationRate(
  hint: Hint,
  codeLength: number,
  digitRange: [number, number],
): { eliminationRate: number; candidatesAfter: number; candidatesBefore: number } {
  const allCandidates = generateAllGuesses(codeLength, digitRange);
  const candidatesBefore = allCandidates.length;
  let survivors = 0;

  for (const candidate of allCandidates) {
    const { correct_placed, correct_misplaced } = evaluateGuess(
      candidate,
      hint.digits,
    );
    if (
      correct_placed === hint.correct_placed &&
      correct_misplaced === hint.correct_misplaced
    ) {
      survivors++;
    }
  }

  const eliminationRate =
    candidatesBefore > 0 ? 1.0 - survivors / candidatesBefore : 0.0;
  return { eliminationRate, candidatesAfter: survivors, candidatesBefore };
}

export function generateHintPool(
  solution: readonly number[],
  digitRange: [number, number] = [0, 9],
): ScoredHint[] {
  const codeLength = solution.length;
  const allGuesses = generateAllGuesses(codeLength, digitRange);

  const rawHints: Array<{
    hint: Hint;
    eliminationRate: number;
    candidatesBefore: number;
    candidatesAfter: number;
    degenerate: DegenerateType;
    powerUp: PowerUpTier | null;
    distinctDigitsTested: number;
    uniqueCorrectDigits: number;
  }> = [];

  for (const guess of allGuesses) {
    if (arraysEqual(guess, solution)) {
      continue;
    }

    const { correct_placed, correct_misplaced } = evaluateGuess(
      solution,
      guess,
    );

    const hint: Hint = {
      digits: [...guess],
      correct_placed,
      correct_misplaced,
    };

    const { eliminationRate, candidatesAfter, candidatesBefore } =
      computeEliminationRate(hint, codeLength, digitRange);

    const degenerate = classifyDegenerate(
      solution,
      guess,
      correct_placed,
      correct_misplaced,
    );
    const powerUp = classifyPowerUp(degenerate);

    const distinctDigitsTested = new Set(guess).size;
    const solutionSet = new Set(solution);
    const uniqueCorrectDigits = [...new Set(guess)].filter((d) =>
      solutionSet.has(d),
    ).length;

    rawHints.push({
      hint,
      eliminationRate,
      candidatesBefore,
      candidatesAfter,
      degenerate,
      powerUp,
      distinctDigitsTested,
      uniqueCorrectDigits,
    });
  }

  rawHints.sort((a, b) => b.eliminationRate - a.eliminationRate);

  const total = rawHints.length;
  const scoredHints: ScoredHint[] = rawHints.map((h, i) => {
    const percentile = total > 1 ? (total - i - 1) / total : 0.0;
    const rarity = classifyRarity(percentile);

    return {
      hint: h.hint,
      rarity,
      elimination_rate: h.eliminationRate,
      percentile,
      degenerate_type: h.degenerate,
      power_up: h.powerUp,
      candidates_before: h.candidatesBefore,
      candidates_after: h.candidatesAfter,
      distinct_digits_tested: h.distinctDigitsTested,
      unique_correct_digits: h.uniqueCorrectDigits,
    };
  });

  scoredHints.sort((a, b) => {
    if (b.elimination_rate !== a.elimination_rate) {
      return b.elimination_rate - a.elimination_rate;
    }
    const aDigits = a.hint.digits.join(",");
    const bDigits = b.hint.digits.join(",");
    return aDigits < bDigits ? -1 : aDigits > bDigits ? 1 : 0;
  });

  return scoredHints;
}
